/**
 * View types of a stored voiceprint: what a person's voice is kept as, where it came from and
 * where it stands. A voiceprint is shown by its mark (`VoiceGlyph`), drawn from its id alone: a
 * handle for the eye, never evidence. The voice sample's own screens are typed in
 * `voice-types.ts`; the voices of a recording in `recording/`, the comparison of two in `match/`.
 *
 * Served: the voiceprints of a lead and of a pair, and a subject's open voice leads. Not built
 * on the server yet: naming the voices of a questioning and a person's list of voiceprints; for
 * those, these types serve the screens designed for them.
 */
import type { MatchBand } from '../types'

/** From a guided sample, or from one voice of a questioning. */
export type VoiceprintOrigin = 'sample' | 'questioning'

export const VOICEPRINT_ORIGINS: readonly VoiceprintOrigin[] = ['sample', 'questioning']

/** Being computed, kept, not made for too little speech, or failed. */
export type VoiceprintState = 'processing' | 'ready' | 'no_speech' | 'failed'

export const VOICEPRINT_STATES: readonly VoiceprintState[] = [
  'processing',
  'ready',
  'no_speech',
  'failed',
]

/** Why the sound of a voiceprint's source reads low: about the recording, never the speaker. */
export type VoiceQualityFlag = 'too_short' | 'noisy' | 'clipped' | 'overlapping_speech'

export const VOICE_QUALITY_FLAGS: readonly VoiceQualityFlag[] = [
  'too_short',
  'noisy',
  'clipped',
  'overlapping_speech',
]

/** The sound of what a voiceprint was made from, with the reasons when it is low. */
export interface VoiceQualityView {
  level: 'ok' | 'low'
  flags: readonly VoiceQualityFlag[]
}

/** A voiceprint as a list of people names it: its mark, where it came from, where it stands. */
export interface VoiceprintBrief {
  /** The seed of its mark. */
  id: string
  /** The first four hex characters of the id, shown in mono beside the mark: "7f3a". */
  shortId: string
  origin: VoiceprintOrigin
  state: VoiceprintState
}

/** One stored voiceprint of a person. */
export interface VoiceprintView extends VoiceprintBrief {
  /** The sample or the recording it was made from. */
  captureId: string
  /** Which voice of that recording; null for a sample. */
  voice: number | null
  encounterRef: string | null
  createdAt: string | null
  /** Net speech it was made from; null until measured. */
  speechMs: number | null
  /** Null where the read does not judge the sound: nothing is drawn then. */
  quality: VoiceQualityView | null
  /** Comparisons use it: it is kept, and the server counts it among the references. */
  used: boolean
  /** Pairs with another person's voiceprint that wait for an analyst. */
  pendingCandidates: number
  /** Auth-gated path of a clip of this voiceprint's speech; null while there is none to play. */
  audioUrl: string | null
}

/**
 * A person's open voice leads: how many, the comparison the pill opens (the first lead, by the
 * list's order) and the person's own voiceprint in it.
 */
export interface SubjectVoiceLeads {
  count: number
  pairId: string | null
  voiceprintId: string | null
}

/**
 * The first open voice lead of a subject, as its page keeps it: the pair the header's pill opens
 * the voice comparison on, and the subject's own voiceprint in that pair (a merge made from there
 * keeps this subject unless the analyst swaps it).
 */
export interface SubjectVoiceLead {
  pairId: string
  voiceprintId: string | null
}

/** What a comparison looked at, as the server judged it: shown, never re-derived. */
export interface VoiceCondition {
  id: 'similarity' | 'quality' | 'agreement' | 'margin'
  met: boolean
  /** The figure behind it; null when there is none to show. */
  value: number | null
}

/**
 * The strength a voice comparison may show, as the server wrote it on the pair: `weak` between
 * its two thresholds (where strangers and one speaker overlap), `possible` from the upper one.
 * `strong` stays unused until an evaluation. Every band is provisional.
 */
export type VoiceBand = MatchBand

/**
 * What a voiceprint's tag needs: its brief, and where the read carries them, the voice it was
 * made from and whether comparisons use it (a brief without the latter reads as used once kept).
 */
export type VoiceprintTagData = VoiceprintBrief & Partial<Pick<VoiceprintView, 'voice' | 'used'>>
